import React from 'react';
import { Link } from 'react-router-dom';

function PlayLink({ to, children }) {
  return (
    <Link to={to} className="content-article__play-link">
      {children ?? 'Play this game →'}
    </Link>
  );
}

/** Cozy-five game sections for /how-to-play. */
export default function HowToPlayGameSections() {
  return (
    <>
      <section id="word-master" className="content-article__section" aria-labelledby="heading-word-master">
        <h2 id="heading-word-master">Word Master</h2>
        <PlayLink to="/word-master" />
        <p>
          Word Master is a five-letter guessing game in the spirit of Wordle: you have a limited number of tries to
          discover the hidden word. Each guess must be a real word from the game dictionary. After every guess you get
          color feedback for each letter so you can narrow down the answer.
        </p>
        <h3>Rules</h3>
        <ul>
          <li>Each guess must be exactly five letters.</li>
          <li>Only valid dictionary guesses are accepted.</li>
          <li>
            <strong>Green</strong> means the letter is correct and in the right position. <strong>Yellow</strong> means
            the letter appears in the word but in a different spot. <strong>Gray</strong> means that letter does not
            appear in the word (or all copies are already accounted for).</li>
          <li>Daily mode gives everyone the same secret word for the day; practice picks a fresh word for extra rounds.</li>
        </ul>
        <h3>Tips</h3>
        <ul>
          <li>Open with a word that uses several common vowels and consonants to maximize information.</li>
          <li>Pay attention to duplicate letters: a green or yellow does not always mean there is a second instance.</li>
          <li>Use the keyboard colors as a running memory of what you have eliminated.</li>
          <li>If you play daily, note that a new puzzle starts on your device’s local calendar day.</li>
        </ul>
        <h3>Quick FAQ</h3>
        <ul>
          <li>
            <strong>Can I replay today’s daily?</strong> Usually the daily is one completion per day; use practice for
            unlimited similar rounds.
          </li>
          <li>
            <strong>Does signing in matter?</strong> Optional for basic play; recommended if you want streaks and
            leaderboard credit where available.
          </li>
        </ul>
      </section>

      <section id="letter-hive" className="content-article__section" aria-labelledby="heading-letter-hive">
        <h2 id="heading-letter-hive">Letter Hive</h2>
        <PlayLink to="/letter-hive" />
        <p>
          Letter Hive is inspired by Spelling Bee–style puzzles: seven letters appear in a hive with one mandatory
          center letter. Build words using only those letters, including the center every time. Longer words and special
          achievements like pangrams (using all seven letters) score extra.
        </p>
        <h3>Rules</h3>
        <ul>
          <li>Minimum word length applies (commonly four letters).</li>
          <li>Every word must include the center letter.</li>
          <li>Letters may be reused; you cannot use letters outside the hive.</li>
          <li>Daily hive is shared for the day; practice uses a fresh hive.</li>
        </ul>
        <h3>Tips</h3>
        <ul>
          <li>Build off short words by adding prefixes (RE-, UN-) or suffixes (-S, -ED, -ING) when allowed.</li>
          <li>Hunt for a pangram early — it often boosts your score sharply.</li>
          <li>Work through letter pairs that appear in many English roots (TH, CH, ST).</li>
        </ul>
        <h3>Quick FAQ</h3>
        <ul>
          <li>
            <strong>Why is my word not accepted?</strong> It might be too short, miss the center, use a disallowed letter,
            or not be in the dictionary.
          </li>
        </ul>
      </section>

      <section id="word-rungs" className="content-article__section" aria-labelledby="heading-word-rungs">
        <h2 id="heading-word-rungs">Word Rungs</h2>
        <PlayLink to="/word-rungs" />
        <p>
          Word Rungs is a word-ladder puzzle: move from a start word to a goal word by changing one letter at a time,
          with each intermediate step also a valid word. The daily puzzle is the same ladder for everyone; practice
          generates new challenges.
        </p>
        <h3>Rules</h3>
        <ul>
          <li>Each step must differ by exactly one letter in the same word length.</li>
          <li>Only dictionary words count as valid rungs.</li>
          <li>Complete the chain within any move limits the mode describes.</li>
        </ul>
        <h3>Tips</h3>
        <ul>
          <li>Compare start and goal letter-by-letter to see which positions must flip.</li>
          <li>Think of “bridge” words that share letters with both ends.</li>
          <li>Work from both ends toward the middle if you get stuck.</li>
        </ul>
        <h3>Quick FAQ</h3>
        <ul>
          <li>
            <strong>Does order matter?</strong> You must follow valid one-letter edits; reordering the same set of words
            is not valid if the chain breaks rules.
          </li>
        </ul>
      </section>

      <section id="crossword-full" className="content-article__section" aria-labelledby="heading-crossword-full">
        <h2 id="heading-crossword-full">Word Square</h2>
        <PlayLink to="/crossword-full" />
        <p>
          Word Square is a daily 5×5 fill-in puzzle: every row and every column must form a valid five-letter English
          word. There are no black squares — just a compact grid and row/column clues. Take your time; a timer runs for
          scoring but there is no countdown pressure.
        </p>
        <h3>Rules</h3>
        <ul>
          <li>Fill each white cell so each row and column spells a valid five-letter word.</li>
          <li>Use row and column clues to guide each entry; crossing letters must agree.</li>
          <li>The daily puzzle changes at <strong>00:00 UTC</strong> so everyone worldwide gets the same grid.</li>
          <li>Progress may be saved locally in the browser; sign in to record your best score and streak.</li>
        </ul>
        <h3>Tips</h3>
        <ul>
          <li>Start with the row or column clue that feels most familiar.</li>
          <li>Let crossing letters confirm partial answers before committing.</li>
          <li>Skip a tricky line and return once you have more letters from the other direction.</li>
        </ul>
        <h3>Quick FAQ</h3>
        <ul>
          <li>
            <strong>Mobile typing?</strong> Tap a cell, then use the on-screen or device keyboard.
          </li>
          <li>
            <strong>Is this a newspaper crossword?</strong> No — it is a word square (rows and columns are all
            five-letter words), not a traditional blocked crossword.
          </li>
        </ul>
      </section>

      <section id="crossword-mini" className="content-article__section" aria-labelledby="heading-crossword-mini">
        <h2 id="heading-crossword-mini">Hidden Words</h2>
        <PlayLink to="/crossword" />
        <p>
          Hidden Words is a calm word-search puzzle: hidden words lie in straight lines — across, down, and diagonal.
          Tap letters one at a time (no drag), then submit when your path matches a hidden word. Find every word to
          clear the board.
        </p>
        <h3>Rules</h3>
        <ul>
          <li>
            <strong>Tap</strong> letters in order along one straight line (including diagonals), one neighboring cell at a time
            with no skips. Tap <strong>Submit word</strong> when your selection matches a hidden word forward or backward along
            that line.
          </li>
          <li>
            Hidden words are <strong>4–8 letters</strong>. Use <strong>Clear</strong> to reset the selection; tap the last
            letter again to undo one step.
          </li>
          <li>Found words are tracked until you have cleared every hidden word.</li>
          <li>
            You have <strong>three Hint</strong> uses per puzzle. Each picks a random unfound word you have not hinted
            yet and <strong>highlights its path</strong> on the grid until you solve it (small chips also show 💡).
            Submitting that word earns <strong>half points</strong> (floored), minimum <strong>5</strong>.
          </li>
          <li>
            The daily grid is <strong>8×8</strong> (same puzzle as the Letter Quest app); everyone gets the same words for
            each local calendar day. Words found <strong>without</strong> a hint score <strong>5 points per letter</strong>; plus a{' '}
            <strong>50-point completion bonus</strong> when you find them all.
          </li>
          <li>
            Use <strong>Submit Score</strong> when you are finished to save progress if you are signed in (web).
          </li>
        </ul>
        <h3>Tips</h3>
        <ul>
          <li>Start with the longest words; they stand out quickly on an 8×8 board.</li>
          <li>Check all eight directions if diagonals are enabled.</li>
          <li>Crossing letters can confirm partial matches quickly.</li>
        </ul>
        <h3>Quick FAQ</h3>
        <ul>
          <li>
            <strong>Word not submitting?</strong> Your path must stay on one straight line with no skipped cells, and the
            spelling must match a hidden word exactly (forward or backward).
          </li>
        </ul>
      </section>
    </>
  );
}
